import {
  ELinkColorVariant,
  ELinkVariant,
  ETagVariant,
  ETextStyleVariant,
  EToastVariant,
  GeneralPaymentMethodAmexIcon,
  GeneralPaymentMethodCardIcon,
  GeneralPaymentMethodDinersclubIcon,
  GeneralPaymentMethodDiscoverIcon,
  GeneralPaymentMethodJcbIcon,
  GeneralPaymentMethodMastercardIcon,
  GeneralPaymentMethodVisaIcon,
  Grid,
  GridItem,
  Link,
  Tag,
  Text,
} from '@outdoorsyco/bonfire';
import React, { ComponentType, SVGProps, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useToast } from '@/hooks/useToast';
import { deleteUserCards, ICard, patchUserCards } from '@/redux/modules/creditCards';
import { EUserCardBrand } from '@/services/types/booking/card';
import { useThunkDispatch } from '@/utility/redux/dispatch';

type TPaymentMethodItemProps = {
  userCard: ICard;
  refreshUserCards: () => void;
};

export const PaymentMethodItem = ({ userCard, refreshUserCards }: TPaymentMethodItemProps) => {
  const intl = useIntl();
  const dispatch = useThunkDispatch();
  const { showToast } = useToast();

  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const cardInfo = useBrandInfo(userCard.brand);
  const CardInfoIcon = cardInfo.icon;
  const cardLabel = `${userCard.brand} ${userCard.last_four}`;
  const expireDate = `${
    userCard.exp_month <= 10 ? '0' + userCard.exp_month : userCard.exp_month
  }/${userCard.exp_year}`;

  const handleSetAsDefault = async () => {
    try {
      setIsUpdating(true);
      await dispatch(
        patchUserCards(userCard.owner_id, userCard.id, {
          default: true,
        }),
      );

      await refreshUserCards();
      setIsUpdating(false);
      showToast(
        EToastVariant.Success,
        intl.formatMessage({
          defaultMessage: 'Success',
          id: 'xrKHS6',
        }),
        intl.formatMessage({
          defaultMessage: 'Default payment method changed successfully.',
          id: 'FmWg6x',
        }),
      );
    } catch {
      await refreshUserCards();
      setIsUpdating(false);

      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          defaultMessage: 'Error',
          id: 'KN7zKn',
        }),
        intl.formatMessage({
          defaultMessage: 'Failed to set default payment method.',
          id: 'bUEpqw',
        }),
      );
    }
  };

  const handleRemove = async () => {
    try {
      setIsDeleting(true);
      await dispatch(deleteUserCards(userCard.owner_id, userCard.id));
      await refreshUserCards();
      showToast(
        EToastVariant.Success,
        intl.formatMessage({
          defaultMessage: 'Success',
          id: 'xrKHS6',
        }),
        intl.formatMessage({
          defaultMessage: 'The payment method has been removed.',
          id: 'ysXkO9',
        }),
      );
      setIsDeleting(false);
    } catch (error) {
      await refreshUserCards();
      setIsDeleting(false);

      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          defaultMessage: 'Error',
          id: 'KN7zKn',
        }),
        error
          ? error
          : intl.formatMessage({
              defaultMessage: 'Failed to delete payment method.',
              id: 'snixt4',
            }),
      );
    }
  };

  return (
    <Grid rowSpacing={3} columnSpacing={0}>
      <GridItem xs={12} sm={12} md={10} lg={10} className="flex flex-col">
        <div className="flex flex-start gap-4">
          <CardInfoIcon aria-label={cardInfo.cardName} width="32px" height="32px" />
          <Text
            variant={ETextStyleVariant.MediumRegular}
            className="flex items-center overflow-hidden capitalize text-ellipsis whitespace-nowrap">
            {cardLabel}
          </Text>
          {userCard.default && (
            <Tag
              label={intl.formatMessage({
                defaultMessage: 'Default',
                id: 'lKv8ex',
              })}
              variant={ETagVariant.Gray}
            />
          )}
        </div>
        <Text variant={ETextStyleVariant.SmallRegular}>
          <FormattedMessage
            defaultMessage="Exp: {expireDate}"
            id="5/WosX"
            values={{ expireDate }}
          />
        </Text>
      </GridItem>
      <GridItem xs={12} sm={12} md={2} lg={2} className="flex justify-start md:justify-end">
        <div className="flex justify-between w-full gap-2 whitespace-nowrap md:flex-col md:items-end md:justify-center">
          {!userCard.default && (
            <>
              <Link
                label={intl.formatMessage({
                  defaultMessage: 'Set as default',
                  id: 'z+Zrln',
                })}
                colorVariant={ELinkColorVariant.Primary}
                variant={ELinkVariant.Underline}
                onClick={handleSetAsDefault}
                disabled={isUpdating || isDeleting}
              />
              <Link
                label={intl.formatMessage({
                  defaultMessage: 'Remove',
                  id: 'G/yZLu',
                })}
                colorVariant={ELinkColorVariant.Danger}
                variant={ELinkVariant.Underline}
                onClick={handleRemove}
                disabled={isUpdating || isDeleting}
              />
            </>
          )}
        </div>
      </GridItem>
    </Grid>
  );
};

type TBrandInfo = {
  cardName: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
};

const useBrandInfo = (brand = ''): TBrandInfo => {
  const intl = useIntl();

  let friendlyName = 'Unknown';
  let icon = GeneralPaymentMethodCardIcon;

  switch (brand.toLowerCase()) {
    case EUserCardBrand.Visa:
      icon = GeneralPaymentMethodVisaIcon;
      friendlyName = intl.formatMessage({
        defaultMessage: 'Visa Credit Card',
        id: 'Dfn1BD',
      });
      break;
    case EUserCardBrand.AmericanExpress:
      icon = GeneralPaymentMethodAmexIcon;
      friendlyName = intl.formatMessage({
        defaultMessage: 'American Express Credit Card',
        id: 'bpIPPj',
      });
      break;
    case EUserCardBrand.Mastercard:
      icon = GeneralPaymentMethodMastercardIcon;
      friendlyName = intl.formatMessage({
        defaultMessage: 'MasterCard Credit Card',
        id: '22ir6r',
      });
      break;
    case EUserCardBrand.Discover:
      icon = GeneralPaymentMethodDiscoverIcon;
      friendlyName = intl.formatMessage({
        defaultMessage: 'Discover Credit Card',
        id: 'w6YaeB',
      });
      break;
    case EUserCardBrand.DinersClub:
      icon = GeneralPaymentMethodDinersclubIcon;
      friendlyName = intl.formatMessage({
        defaultMessage: 'Diners ClubCredit Card',
        id: 'qL3ZWp',
      });
      break;
    case EUserCardBrand.JSB:
      icon = GeneralPaymentMethodJcbIcon;
      friendlyName = intl.formatMessage({
        defaultMessage: 'JCB Credit Card',
        id: '1w7Qzh',
      });
      break;
  }

  return {
    cardName: friendlyName,
    icon,
  };
};
